import { useState } from 'react';

import styled from 'styled-components';

import { FormValidatorType, useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { DynamicSelectOption } from '@components/form/dynamic-fields';
import { useListOptions } from '@components/integrations/lists';
import { SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { useConditionComparators } from './comparators';
import { findMappedField, findValueOptions, findValueType } from './field-utils';

type ComparatorComponentProps<T> = {
  condition: {
    id?: string;
    field: VariableValue;
    comparator?: string;
    value?: any;
  };
  form: FormInstance<T>;
  variableGroups: FieldMappingGroup[];
  isWebhook?: boolean;
  index?: number;
  elementIdKey?: keyof T;
  comparatorIdKey?: keyof T;
  valueIdKey?: keyof T;
  additionalComparators?: SelectOption[];
  filterComparators?: (comparators: SelectOption[]) => SelectOption[];
};

const EmptyDisabledInput = ({ label }: { label: string }) => <TextField label={label} isDisabled />;

export const ComparatorComponent = <T extends Record<string, any>>({
  condition,
  form: Form,
  variableGroups,
  isWebhook = false,
  index,
  elementIdKey,
  comparatorIdKey,
  valueIdKey,
  additionalComparators = [],
  filterComparators,
}: ComparatorComponentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const { store } = useFormContext();
  const formValues = store.useFormValues();

  const { getComparator } = useConditionComparators();

  const BASE_COMPARATORS = [...additionalComparators, ...getComparator()];
  const DATE_COMPARATORS = [...additionalComparators, ...getComparator('date')];
  const FILE_COMPARATORS = [...additionalComparators, ...getComparator('file')];
  const BOOLEAN_COMPARATORS = [...additionalComparators, ...getComparator('boolean')];
  const NUMBER_COMPARATORS = [...additionalComparators, ...getComparator('number')];
  const SELECT_COMPARATORS = [...additionalComparators, ...getComparator('select')];
  const SELECT_MULTI_COMPARATORS = [...additionalComparators, ...getComparator('multi_select')];

  const validators = ['required'] as FormValidatorType[];

  const formValue = elementIdKey
    ? (formValues[elementIdKey as keyof typeof formValues] as VariableValue)
    : (formValues.field as VariableValue) || condition.field;

  const mappedField = findMappedField(formValue, variableGroups);
  const valueType = findValueType(mappedField);
  const inlineOptions = findValueOptions(mappedField);
  const listOptions = useListOptions(mappedField?.optionsFromListId, mappedField?.excludedOptions);
  const valueOptions = inlineOptions.length > 0 ? inlineOptions : listOptions;

  const isMultiComparator = (c: string) => c === 'select_any_of' || c === 'select_none_of';

  const [selectedComparator, setSelectedComparator] = useState<string>(condition.comparator ?? '');
  const [allowEmptyWebhook, setAllowEmptyWebhook] = useState(isWebhook && !condition.value);

  const isMulti = isMultiComparator(selectedComparator);
  const valueTypeChanged = isMulti !== isMultiComparator(condition.comparator ?? '');
  const valueFieldKey = isMulti ? 'multi' : 'single';
  const valueDefaultValue = valueTypeChanged ? undefined : condition.value;

  const WebhookAllowEmpty = () =>
    isWebhook ? (
      <Switch
        label={t('fieldmapInJourney')}
        key={`allow-${String(allowEmptyWebhook)}`}
        onChange={() => setAllowEmptyWebhook(!allowEmptyWebhook)}
        defaultValue={allowEmptyWebhook}
      />
    ) : null;

  const allowEmpty =
    allowEmptyWebhook ||
    selectedComparator.includes('empty') ||
    selectedComparator.includes('changes_value');

  const applyFilter = (comparators: SelectOption[]) => {
    return filterComparators ? filterComparators(comparators) : comparators;
  };

  const comparatorId = (comparatorIdKey ?? 'comparator') as any;
  const valueId = (valueIdKey ?? 'value') as any;

  switch (valueType) {
    case 'date':
      return (
        <Stack space={4}>
          <Stack space={2}>
            <Form.Select
              id={comparatorId}
              options={applyFilter(DATE_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.DatePicker
                id={valueId}
                label={t('dateLabel')}
                validators={validators}
                defaultValue={(condition.value as string) || undefined}
              />
            )}
          </Stack>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'select':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(SELECT_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : isMulti ? (
              <Form.SelectMulti
                key={valueFieldKey}
                // @ts-ignore
                id={valueId}
                label={t('valuesLabel')}
                options={valueOptions}
                validators={validators}
                defaultValue={valueDefaultValue as DynamicSelectOption[]}
              />
            ) : (
              <Form.Select
                key={valueFieldKey}
                selection="full"
                id={valueId}
                label={t('valueLabel')}
                options={valueOptions}
                defaultValue={valueDefaultValue as SelectOption}
                validators={validators}
                isClearable={false}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'multi_select':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(SELECT_MULTI_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.SelectMulti
                // @ts-ignore
                id={valueId}
                label={t('valuesLabel')}
                options={valueOptions}
                validators={validators}
                defaultValue={condition.value as DynamicSelectOption[]}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'boolean':
      return (
        <ComparatorValueContainer>
          <Form.Select
            id={comparatorId}
            options={applyFilter(BOOLEAN_COMPARATORS)}
            label={t('comparatorLabel')}
            defaultValue={condition.comparator}
            validators={validators}
            isClearable={false}
            onChange={setSelectedComparator}
          />
        </ComparatorValueContainer>
      );
    case 'file':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(FILE_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.TextField
                id={valueId}
                label={t('valueLabel')}
                validators={validators}
                defaultValue={condition.value as string}
                dataTestid={`${valueType}-condition-value-${index}`}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'number':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(NUMBER_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.NumberField
                id={valueId}
                label={t('valueLabel')}
                validators={validators}
                defaultValue={condition.value as number}
                dataTestid={`${valueType}-condition-value-${index}`}
                isDecimal={mappedField?.type === 'decimal'}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    default:
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(BASE_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.TextField
                id={valueId}
                label={t('valueLabel')}
                validators={validators}
                defaultValue={typeof condition.value === 'string' ? condition.value : ''}
                dataTestid={`${valueType}-condition-value-${index}`}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
  }
};

const locale = {
  en: {
    comparatorLabel: 'Comparator',
    valueLabel: 'Value',
    valuesLabel: 'Values',
    dateLabel: 'Date',
    fieldmapInJourney: 'Select value in journey builder',
  },
};

export const ComparatorValueContainer = styled(Stack).attrs({
  orientation: 'vertical',
  space: 2,
})({});
